/* BrokerageDetail — shared detail view used in:
 *   1. The in-app overlay dialog (Sheet) on /brokerages
 *   2. The standalone page at /brokerages/:id (for CRM deep-links)
 *
 * Shows brokerage info, editable notes, interaction log timeline,
 * CRM deep-link copy button, and realtors list.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  MapPin,
  Users,
  Globe,
  ExternalLink,
  Loader2,
  Plus,
  Trash2,
  Copy,
  Check,
  StickyNote,
  Coffee,
  MessageSquare,
  Presentation,
  Package,
  UserCircle,
  CalendarClock,
  Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import RealtorPicker from "@/components/brokerages/RealtorPicker";

const INTERACTION_TYPES = [
  { value: "front_desk_contact", label: "Front desk contact", icon: UserCircle },
  { value: "conversation", label: "Conversation", icon: MessageSquare },
  { value: "presentation", label: "Presentation", icon: Presentation },
  { value: "materials_dropoff", label: "Materials drop-off", icon: Package },
  { value: "coffee_visit", label: "Coffee visit", icon: Coffee },
  { value: "note", label: "Note", icon: StickyNote },
];

const CRM_PUBLIC_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function BrokerageDetail({ brokerage: initialBrokerage, onUpdated, onClose }) {
  const [brokerage, setBrokerage] = useState(initialBrokerage);
  const [notes, setNotes] = useState(initialBrokerage?.notes || "");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesDirty, setNotesDirty] = useState(false);
  const [realtors, setRealtors] = useState([]);
  const [loadingRealtors, setLoadingRealtors] = useState(false);
  const [copied, setCopied] = useState(false);

  const crmLink = brokerage ? `${CRM_PUBLIC_URL}/brokerages/${brokerage.id}` : "";

  // Load realtors for this brokerage
  useEffect(() => {
    if (!brokerage?.id) return;
    let cancelled = false;
    setLoadingRealtors(true);
    api
      .get("/realtors", { params: { brokerage_name: brokerage.name, limit: 200 } })
      .then(({ data }) => {
        if (!cancelled) setRealtors(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingRealtors(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the brokerage identity changes, not on every rename
  }, [brokerage?.id]);

  // Sync if parent updates the brokerage
  useEffect(() => {
    if (initialBrokerage) {
      setBrokerage(initialBrokerage);
      setNotes(initialBrokerage.notes || "");
      setNotesDirty(false);
    }
  }, [initialBrokerage]);

  const handleUpdate = useCallback(
    (updated) => {
      setBrokerage(updated);
      if (onUpdated) onUpdated(updated);
    },
    [onUpdated],
  );

  // Persist Owner / Brokerage-Contact selection immediately — the picker
  // calls this on every add/remove so the user never has to hit a Save
  // button. Optimistic update: apply locally, then reconcile with server.
  const savePeople = async (patch) => {
    setBrokerage((prev) => ({ ...prev, ...patch }));
    try {
      const { data } = await api.patch(`/brokerages/${brokerage.id}`, patch);
      handleUpdate(data);
    } catch (e) {
      toast.error("Couldn't save assignment", { description: formatApiError(e) });
    }
  };

  const selectedOwners = brokerage?.owner_realtor_ids || [];
  const selectedContacts = brokerage?.contact_realtor_ids || [];

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      const { data } = await api.patch(`/brokerages/${brokerage.id}`, { notes });
      toast.success("Notes saved");
      handleUpdate(data);
      setNotesDirty(false);
    } catch (e) {
      toast.error("Failed to save notes", { description: formatApiError(e) });
    } finally {
      setNotesSaving(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(crmLink);
      setCopied(true);
      toast.success("CRM link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for iframe contexts
      const ta = document.createElement("textarea");
      ta.value = crmLink;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      toast.success("CRM link copied");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!brokerage) return null;

  return (
    <div className="space-y-6" data-testid="brokerage-detail">
      {/* Header */}
      <div>
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <Building2 className="h-6 w-6 text-slate-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-display font-bold text-slate-900 truncate">
              {brokerage.name}
            </h2>
            {brokerage.office_address ? (
              <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                <MapPin className="h-3.5 w-3.5 shrink-0" /> {brokerage.office_address}
              </p>
            ) : (
              <p className="text-sm text-slate-400 italic mt-0.5">No address on file</p>
            )}
          </div>
        </div>

        {/* Quick stats row */}
        <div className="flex flex-wrap gap-3 mt-3">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">
            <Users className="h-3 w-3" /> {brokerage.realtor_count || 0} realtors
          </span>
          {brokerage.city ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs text-blue-700">
              <MapPin className="h-3 w-3" /> {brokerage.city}
            </span>
          ) : null}
          {brokerage.website_url ? (
            <a
              href={brokerage.website_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 hover:underline"
            >
              <Globe className="h-3 w-3" /> Website <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      </div>

      {/* CRM Deep-Link */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-1.5">
          <Link2 className="h-3.5 w-3.5" /> CRM Link
        </Label>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate text-slate-700">
            {crmLink}
          </code>
          <Button
            variant="outline"
            size="sm"
            onClick={copyLink}
            className="shrink-0 min-h-[36px]"
            data-testid="brokerage-copy-crm-link"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          Bookmark or add to a contact card &mdash; opens this brokerage{"'"}s full detail page.
        </p>
      </div>

      {/* People-in-charge — Owner + Brokerage Contact. Populated from
          the realtors list of THIS brokerage. Each chip links to the
          realtor profile, so the user can click through to grab a number
          when booking a meeting. Multi-select on both. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
            Owner{selectedOwners.length !== 1 ? "s" : ""}
          </Label>
          <RealtorPicker
            value={brokerage?.owner_realtor_ids || []}
            options={realtors}
            onChange={(next) => savePeople({ owner_realtor_ids: next })}
            placeholder="Add an owner…"
            testid="brokerage-owner-picker"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
            Brokerage contact{selectedContacts.length !== 1 ? "s" : ""}
          </Label>
          <RealtorPicker
            value={brokerage?.contact_realtor_ids || []}
            options={realtors}
            onChange={(next) => savePeople({ contact_realtor_ids: next })}
            placeholder="Add a contact…"
            testid="brokerage-contact-picker"
          />
        </div>
      </div>

      {/* Notes */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 block">
          Notes
        </Label>
        <Textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setNotesDirty(true);
          }}
          placeholder="Front desk contact names, coffee preferences, presentation history, materials dropped off…"
          className="min-h-[100px] text-sm"
          data-testid="brokerage-notes-textarea"
        />
        {notesDirty ? (
          <Button
            onClick={saveNotes}
            disabled={notesSaving}
            size="sm"
            className="mt-2 text-white min-h-[36px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="brokerage-notes-save"
          >
            {notesSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save notes
          </Button>
        ) : null}
      </div>

      {/* Interaction Log */}
      <InteractionLogPanel brokerage={brokerage} onUpdated={handleUpdate} />

      {/* Realtors */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Realtors at this brokerage
        </Label>
        {loadingRealtors ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : realtors.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No realtors found for this brokerage.</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {realtors.map((r) => (
              <Link
                key={r.id}
                to={`/realtors/${r.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2.5 py-1 text-xs text-slate-700 hover:border-slate-400 transition-colors"
              >
                {r.name || `${r.first_name || ""} ${r.last_name || ""}`.trim() || "—"}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InteractionLogPanel({ brokerage, onUpdated }) {
  const [entryType, setEntryType] = useState("note");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const log = Array.isArray(brokerage.interaction_log) ? brokerage.interaction_log : [];

  const addEntry = async () => {
    if (!note.trim() && entryType === "note") {
      toast.error("Add a note before saving");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/brokerages/${brokerage.id}/interaction`, {
        type: entryType,
        note: note.trim(),
      });
      toast.success("Interaction logged");
      setNote("");
      onUpdated(data);
    } catch (e) {
      toast.error("Save failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async (entryId) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/brokerages/${brokerage.id}/interaction/${entryId}`);
      toast.success("Entry removed");
      onUpdated(data);
    } catch (e) {
      toast.error("Delete failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-t border-slate-200 pt-4 space-y-3" data-testid="brokerage-interaction-log">
      <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2 font-display">
        <CalendarClock className="h-4 w-4 text-slate-500" /> Interaction timeline
      </h4>

      {/* Add entry */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={entryType} onValueChange={setEntryType}>
            <SelectTrigger className="min-h-[44px] sm:w-52" data-testid="interaction-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTERACTION_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Details — who, what, when…"
            className="min-h-[44px] flex-1"
            data-testid="interaction-note-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                addEntry();
              }
            }}
          />
          <Button
            onClick={addEntry}
            disabled={busy}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="interaction-add-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Log
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {log.length === 0 ? (
        <p className="text-xs text-slate-500 italic px-1">
          No interactions logged yet. Record front desk contacts, presentations, coffee visits, and more.
        </p>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {[...log]
            .sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))
            .map((e) => {
              const meta = INTERACTION_TYPES.find((t) => t.value === e.type) || INTERACTION_TYPES[5];
              const Icon = meta.icon;
              return (
                <li
                  key={e.id}
                  className="flex items-start gap-2 text-sm border border-slate-200 rounded-md p-2 bg-white"
                  data-testid={`interaction-entry-${e.id}`}
                >
                  <Icon className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{meta.label}</span>
                      <span className="text-xs text-slate-500 tabular-nums">
                        {formatRelativeDate(e.timestamp)}
                      </span>
                      {e.actor_email ? (
                        <span className="text-xs text-slate-400 truncate">· {e.actor_email}</span>
                      ) : null}
                    </div>
                    {e.note ? (
                      <div className="text-slate-700 text-sm mt-0.5 whitespace-pre-wrap">{e.note}</div>
                    ) : null}
                  </div>
                  <button
                    onClick={() => removeEntry(e.id)}
                    disabled={busy}
                    className="text-slate-400 hover:text-red-600 p-1"
                    aria-label="Remove entry"
                    data-testid={`interaction-delete-${e.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </section>
  );
}

function formatRelativeDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return "today";
    if (diffDays === 1) return "yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}
